import React from 'react'
import PropTypes from "prop-types"
import i18n from 'i18n/index.js'
import ExcursionImage from "components/products/ExcursionImage";
import { money, from_price } from "components/utils/Money.jsx";
import { duration_time_by_duration_type } from "components/utils/Duration.jsx";
import { buildLocalePath } from "components/utils/Locale";

function zoneSlug(product) {
  return (product && product.zone && product.zone.name.toLowerCase().split(' ').join('-')) || '';
}

function hoverTintFromProductId(id) {
  const s = String(id ?? '');
  let x = 0;
  for (let i = 0; i < s.length; i++) {
    x = (x * 31 + s.charCodeAt(i)) >>> 0;
  }
  const k = 12;
  const hue = ((x % k) * (360 / k)) | 0;
  const sat = 66 + ((x >>> 4) % 4) * 6;
  const light = 40 + ((x >>> 8) % 3) * 5;
  return `hsla(${hue}, ${sat}%, ${light}%, 0.14)`;
}

function Product(props) {
  const variant = props.variant || 'default';
  const isRecommended = variant === 'recommended';

  const renderIncludedFeatures = () => {
    if (props.product.product_includeds && props.product.product_includeds.length > 0) {
      return (
        <div className="feature-included landing">
          <h4>{i18n.t("excursions.what's_included?")}</h4>
          <ul>
            {props.product.product_includeds.map((incl) => (
              <li key={incl.id}>{incl.description}</li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };

  const renderNotIncludedFeatures = () => {
    if (props.product.product_not_includeds && props.product.product_not_includeds.length > 0) {
      return (
        <div className="feature-not-included landing">
          <h4>{i18n.t("excursions.what's_not_included?")}</h4>
          <ul>
            {props.product.product_not_includeds.map((incl) => (
              <li key={incl.id}>{incl.description}</li>
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };

  const createSelectItemDataLayer = () => {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': "select_item",
      'ecommerce': {
        'item_list_id': "products_list",
        'item_list_name': "Products List",
        'items': [
          {
            'item_id': props.product.id,
            'item_name': props.product.title,
            'price': from_price(props.product),
            'item_brand': (props.product.agency ? props.product.agency?.name : "Outdoor Index"),
            'item_category': (props.product.zone && props.product.zone.name || ""),
            'item_variant': 'Excursion',
            'affiliation': 'Outdoor index',
            'quantity': '1'
          }
        ]
      }
    });
  };

  const zs = zoneSlug(props.product);
  const rootClass = [
    'product-card',
    isRecommended ? 'product-card--recommended posts-recommended' : 'slide posts',
    zs,
  ].filter(Boolean).join(' ');
  const productHref = buildLocalePath("products/" + (props.product.slug != null ? props.product.slug : props.product.id));

  const renderExtraInfo = () => {
    if (!props.extra_info) return null;
    if (isRecommended) {
      return (
        <div className="landing-extra-info">
          <div className="landing-product-container">
            <a
              className="landing-product-view h4"
              href={productHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.t('active_admin.landing.more_info')}
            </a>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="landing-extra-info">
          {renderIncludedFeatures()}
          {renderNotIncludedFeatures()}
        </div>
        <div className="landing-product-container">
          <button
            type="button"
            className="button h4"
            onClick={() => {
              const w = window.open(productHref, "_blank");
              if (w) w.opener = null;
            }}
          >
            {i18n.t('active_admin.landing.more_info')}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className={rootClass}>
      <div
        className="product-card__stack"
        style={{ '--product-card-hover-overlay': hoverTintFromProductId(props.product.id) }}
      >
        <a
          className="product-card__cover-link"
          href={productHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => createSelectItemDataLayer()}
          aria-label={props.product.title}
        />
        <div className="free">
          {props.product.private_check &&
            <span className="oi-flex oi-flex-row oi-items-start oi-justify-start oi-text-left oi-w-full">
              <span className="tooltip">
                <span className='mdl-chip brand-label'>
                  <span className='mdl-chip__text'>
                    {i18n.t("ondemand.label")}
                  </span>
                </span>
              </span>
            </span>
          }

          <ExcursionImage
            key={"excursion-image-" + props.product.id}
            product={props.product}
            image_format={props.image_format}
            skeleton_height={(props.skeleton_height ? props.skeleton_height : 352)}
            createSelectItemDataLayer={createSelectItemDataLayer}
            omitLink
          />
        </div>

        <div className="info-slide-main oi-flex oi-flex-column">
          <div className="info-slide oi-flex oi-flex-column">
            {props.product.zone &&
              <p className="zone h7">{props.product.zone.name}</p>
            }

            <h4>{props.product.title}</h4>

            <p className="price h5">
              {i18n.t('from')} {money(props.product)} | {duration_time_by_duration_type(props.product, false)}
            </p>
          </div>
          {props.extra_info && renderExtraInfo()}
        </div>
      </div>
    </div>
  );
}

Product.propTypes = {
  product: PropTypes.object.isRequired,
  image_format: PropTypes.string,
  skeleton_height: PropTypes.number,
  extra_info: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'recommended']),
};

export default Product
